import { ApiError, getModes } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

/**
 * Holds protected routes behind a skeleton until the first authenticated API
 * call succeeds. A 403 routes to /access-denied; any other error bubbles up
 * as a generic failure so a misconfigured backend doesn't silently let
 * in an un-vetted session.
 */
export function AllowlistGate({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { data, error, isLoading } = useQuery({
    queryKey: ["modes"],
    queryFn: getModes,
    enabled: !authLoading && !!user,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 1;
    },
  });

  if (authLoading || isLoading) {
    return <LoadingSkeleton />;
  }
  if (error instanceof ApiError && error.status === 403) {
    return <Navigate to="/access-denied" replace />;
  }
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          Couldn't reach the arena backend. Try refreshing.
        </div>
      </div>
    );
  }
  if (!data) {
    return <LoadingSkeleton />;
  }
  return <>{children}</>;
}

function LoadingSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-2 w-32 animate-pulse rounded bg-muted" />
    </div>
  );
}
